/**
 * Module dependencies.
 */

import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createSearchParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { AreaTrendChart, DonutChart, chartColors } from 'app/components/organisms/charts/charts';
import { useDashboardOverview } from 'app/hooks/requests/dashboard/use-dashboard-overview';
import { useMovementsOverview } from 'app/hooks/requests/use-movements-overview';
import { useMovementsStats } from 'app/hooks/requests/dashboard/use-movements-stats';
import { ExtraActions } from 'app/components/pages/bank-accounts/movements/extra-actions';
import { reduce, sortBy } from 'lodash';
import { formatCurrency } from 'app/core/utils/formatter';
import { Loading } from 'app/components/atoms/loading/loading';
import { cn } from 'app/lib/utils';
import { appRoutes } from 'app/routes';
import BigNumber from 'bignumber.js';
import moment from 'moment';
import {
  AlertTriangle,
  ArrowLeftRight,
  Landmark,
  Layers,
  RefreshCw,
  TrendingDown,
  TrendingUp,
  Wallet
} from 'lucide-react';

/**
 * `StatCard` component.
 */

function StatCard({
  icon,
  label,
  value,
  tone,
  hint,
  onClick,
  onIconClick,
  iconSpin
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  tone?: string;
  hint?: string;
  onClick?: () => void;
  onIconClick?: () => void;
  iconSpin?: boolean;
}): JSX.Element {
  const Tag: any = onClick ? 'button' : 'div';
  const IconTag: any = onIconClick ? 'button' : 'div';

  return (
    <Tag
      className={cn(
        'rounded-xl border border-border bg-card p-5 text-left shadow-sm transition-shadow',
        onClick && 'cursor-pointer hover:shadow-md'
      )}
      onClick={onClick}
      type={onClick ? 'button' : undefined}
    >
      <div className={'flex items-center justify-between gap-3'}>
        <span className={'text-xs font-medium text-muted-foreground'}>{label}</span>

        <IconTag
          className={cn(
            'grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-muted text-muted-foreground [&_svg]:h-[18px] [&_svg]:w-[18px]',
            onIconClick && 'transition-colors hover:bg-muted-foreground/15 hover:text-foreground'
          )}
          onClick={
            onIconClick
              ? (event: React.MouseEvent) => {
                  event.stopPropagation();
                  onIconClick();
                }
              : undefined
          }
          type={onIconClick ? 'button' : undefined}
        >
          <span className={cn(iconSpin && 'animate-spin')}>{icon}</span>
        </IconTag>
      </div>

      <div className={cn('mt-3 text-[26px] font-semibold leading-none tabular-nums text-foreground', tone)}>
        {value}
      </div>

      {!!hint && <div className={'mt-1.5 text-xs text-muted-foreground'}>{hint}</div>}
    </Tag>
  );
}

/**
 * `Panel` component.
 */

function Panel({ title, children }: { title: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className={'rounded-xl border border-border bg-card shadow-sm'}>
      <div className={'border-b border-border px-5 py-3.5 text-sm font-semibold text-foreground'}>{title}</div>

      <div className={'p-5'}>{children}</div>
    </div>
  );
}

/**
 * Export `DashboardOverview` component.
 */

export function DashboardOverview(): JSX.Element {
  const [translate] = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data, isLoading } = useDashboardOverview();
  const { data: movements } = useMovementsOverview(undefined);
  const { data: stats, isFetching: statsFetching } = useMovementsStats();
  const [refreshOpen, setRefreshOpen] = useState(false);

  const dataPoints: any[] = data?.plot?.data?.[0]?.dataPoints ?? [];

  const legend = useMemo(
    () =>
      sortBy(
        dataPoints.map((item: any) => ({
          id: item.metadata.id,
          title: item.indexLabel,
          balance: parseFloat(item.metadata.balance)
        })),
        item => -item.balance
      ),
    [dataPoints]
  );

  const { totalReport, totalNegative, total } = useMemo(
    () =>
      reduce(
        dataPoints,
        (acc, item: any) => {
          if (new BigNumber(item.metadata.balance).isGreaterThanOrEqualTo(0)) {
            acc.totalReport = acc.totalReport.plus(item.metadata.balance);
          } else {
            acc.totalNegative = acc.totalNegative.plus(item.metadata.balance);
          }

          acc.total = new BigNumber(acc.totalReport).plus(acc.totalNegative);

          return acc;
        },
        {
          totalReport: new BigNumber('0'),
          totalNegative: new BigNumber('0'),
          total: new BigNumber('0')
        }
      ),
    [dataPoints]
  );

  const incomeExpenseData = useMemo(() => {
    const series: any[] = movements?.incomeVsExpenses?.data ?? [];

    if (!series.length) {
      return [];
    }

    const income = series.find((item: any) => /income/i.test(item.name))?.dataPoints ?? series[0]?.dataPoints ?? [];
    const expenses = series.find((item: any) => /expense/i.test(item.name))?.dataPoints ?? series[1]?.dataPoints ?? [];

    return income.map((point: any, index: number) => ({
      month: point.label,
      income: point.y ?? 0,
      expenses: expenses[index]?.y ?? 0
    }));
  }, [movements]);

  const monthNet = useMemo(() => {
    const row = incomeExpenseData[moment().month()];

    if (!row) {
      return null;
    }

    return new BigNumber(row.income).minus(row.expenses);
  }, [incomeExpenseData]);

  const donutData = useMemo(
    () => legend.filter(item => item.balance > 0).map(item => ({ name: item.title, value: item.balance })),
    [legend]
  );

  const maxAbs = useMemo(() => Math.max(1, ...legend.map(item => Math.abs(item.balance))), [legend]);

  // Called by the refresh modal after it finishes syncing the bank accounts.
  const refresh = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries(['dashboardOverview']),
      queryClient.invalidateQueries(['movementsOverview']),
      queryClient.invalidateQueries(['movementsStats'])
    ]);
  }, [queryClient]);

  const goToUncategorized = useCallback(() => {
    navigate({
      pathname: appRoutes.dashboard.bankAccounts.movementsManager.base,
      search: `?${createSearchParams({
        filter: JSON.stringify({ orderBy: 'bank_date#desc', category: -1 }),
        filterOpened: 'true'
      } as any)}`
    });
  }, [navigate]);

  return (
    <Loading isLoading={isLoading}>
      {!!data && (
        <div className={'space-y-5'}>
          {/* Bank-account refresh modal, opened from the last-update card */}
          <ExtraActions hideTrigger onOpenChange={setRefreshOpen} open={refreshOpen} refresh={refresh} />

          {/* Balances */}
          <div className={'grid grid-cols-4 gap-4 max-[991px]:grid-cols-2 max-[600px]:grid-cols-1'}>
            <StatCard
              icon={<Wallet />}
              label={translate('dashboard.overview.cards.total')}
              tone={total.isLessThan(0) ? 'text-destructive' : undefined}
              value={formatCurrency(total)}
            />

            <StatCard
              icon={<TrendingUp />}
              label={translate('dashboard.overview.cards.positive')}
              tone={'text-primary'}
              value={formatCurrency(totalReport)}
            />

            <StatCard
              icon={<TrendingDown />}
              label={translate('dashboard.overview.cards.negative')}
              tone={'text-destructive'}
              value={formatCurrency(totalNegative)}
            />

            <StatCard
              icon={<Landmark />}
              label={translate('dashboard.overview.cards.accounts')}
              value={String(legend.length)}
            />
          </div>

          {/* Movements */}
          <div className={'grid grid-cols-4 gap-4 max-[991px]:grid-cols-2 max-[600px]:grid-cols-1'}>
            <StatCard
              icon={<Layers />}
              label={translate('dashboard.overview.movements.total')}
              value={stats ? String(stats.total) : '—'}
            />

            <StatCard
              icon={<AlertTriangle />}
              label={translate('dashboard.overview.movements.uncategorized')}
              onClick={stats && stats.uncategorized > 0 ? goToUncategorized : undefined}
              tone={stats && stats.uncategorized > 0 ? 'text-[#cc7a1a]' : undefined}
              value={stats ? String(stats.uncategorized) : '—'}
            />

            <StatCard
              icon={<ArrowLeftRight />}
              label={translate('dashboard.overview.movements.monthNet')}
              tone={monthNet ? (monthNet.isNegative() ? 'text-destructive' : 'text-primary') : undefined}
              value={monthNet ? formatCurrency(monthNet) : '—'}
            />

            <StatCard
              icon={<RefreshCw />}
              iconSpin={refreshOpen || statsFetching}
              label={translate('dashboard.overview.movements.lastUpdate')}
              onIconClick={() => setRefreshOpen(true)}
              value={stats?.lastDate ? moment(stats.lastDate).format('DD MMM YYYY') : '—'}
            />
          </div>

          {/* Income vs Expenses (full width) */}
          {incomeExpenseData.length > 0 && (
            <Panel title={translate('dashboard.overview.incomeVsExpenses')}>
              <AreaTrendChart
                data={incomeExpenseData}
                series={[
                  { key: 'income', label: translate('common.labels.income'), color: chartColors.primary },
                  { key: 'expenses', label: translate('common.labels.expenses'), color: chartColors.destructive }
                ]}
                valueFormatter={formatCurrency}
                xKey={'month'}
              />
            </Panel>
          )}

          {/* Balance by account: table with bars + donut */}
          <div className={'grid grid-cols-[1.2fr_1fr] gap-4 max-[1200px]:grid-cols-1'}>
            <Panel title={translate('dashboard.overview.byAccount')}>
              <div className={'space-y-1'}>
                {legend.map(item => {
                  const negative = item.balance < 0;
                  const width = `${Math.max(2, (Math.abs(item.balance) / maxAbs) * 100)}%`;

                  return (
                    <div className={'flex items-center gap-3 py-1.5'} key={item.id}>
                      <span className={'w-32 shrink-0 truncate text-sm text-muted-foreground'}>{item.title}</span>

                      <div className={'h-2 flex-1 overflow-hidden rounded-full bg-muted'}>
                        <div
                          className={'h-full rounded-full'}
                          style={{ width, backgroundColor: negative ? chartColors.destructive : chartColors.primary }}
                        />
                      </div>

                      <span
                        className={cn(
                          'w-28 shrink-0 text-right text-sm font-medium tabular-nums',
                          negative ? 'text-destructive' : 'text-foreground'
                        )}
                      >
                        {formatCurrency(item.balance)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </Panel>

            {donutData.length > 0 && (
              <Panel title={translate('dashboard.overview.byAccountShare')}>
                <DonutChart data={donutData} valueFormatter={formatCurrency} />
              </Panel>
            )}
          </div>
        </div>
      )}
    </Loading>
  );
}
